import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Sparkles, Layers } from 'lucide-react';
import api from '../../lib/api';

// --- A/B test for hero subtitle copy ---
// Variant A: focus "Digital Score gratuito" (functional / outcome-driven)
// Variant B: focus "AI DTS parla con te" (conversational / wow-driven)
// Assignment is 50/50, persisted in localStorage so the same user always
// sees the same variant (avoids skewing conversion rates).
const HERO_EXPERIMENT = 'hero_subtitle_v1';
const HERO_VARIANTS = {
  A: {
    label: 'A',
    subtitle: "Il tuo Digital Hub: calcola il tuo Digital Score gratuito in 3 minuti e scopri i 7 passi concreti per far crescere la tua attività.",
  },
  B: {
    label: 'B',
    subtitle: "Il tuo Digital Hub: l'AI DTS parla con te 24/7, ti guida ai servizi giusti e trasforma ogni domanda in un'opportunità misurabile.",
  },
};

// Headline unified on the new owner-approved copy (Oct 2026). Both A/B legs
// render the same text so we no longer skew the test on headline copy; the
// plumbing stays in place for future experiments.
const HEADLINE_EXPERIMENT = 'hero_headline_v2';
const HEADLINE_HTML = (
  <>LA TUA <span className="gradient-text">CRESCITA DIGITALE</span> INIZIA QUI: <span className="gradient-text">SOLUZIONI DIGITALI</span> PER PRIVATI, AZIENDE E PROFESSIONISTI</>
);
const HEADLINE_VARIANTS = {
  A: { label: 'A', html: HEADLINE_HTML },
  B: { label: 'B', html: HEADLINE_HTML },
};

// Primary CTA colour A/B test — isolates the colour impact independently from
// headline/subtitle copy. Variant A keeps the brand lime (default); variant B
// uses an amber that pops against the dark hero bg.
const CTA_COLOR_EXPERIMENT = 'hero_cta_color_v1';
const CTA_COLORS = {
  A: {
    label: 'A',
    bg: '#c6f24e',           // lime (control)
    hover: '#d4ff00',
    text: '#0b1026',
  },
  B: {
    label: 'B',
    bg: '#f59e0b',           // amber (test)
    hover: '#fbbf24',
    text: '#0b1026',
  },
};

function pickVariant(expName) {
  try {
    const key = `ab:${expName}`;
    const existing = localStorage.getItem(key);
    if (existing && (existing === 'A' || existing === 'B')) return existing;
    const v = Math.random() < 0.5 ? 'A' : 'B';
    localStorage.setItem(key, v);
    return v;
  } catch {
    return 'A';
  }
}

function getSessionId() {
  try {
    let sid = localStorage.getItem('dts_session_id');
    if (!sid) {
      sid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('dts_session_id', sid);
    }
    return sid;
  } catch {
    return `tmp-${Date.now().toString(36)}`;
  }
}

export default function Hero() {
  const [variant] = useState(() => pickVariant(HERO_EXPERIMENT));
  const [headlineVariant] = useState(() => pickVariant(HEADLINE_EXPERIMENT));
  const [ctaVariant] = useState(() => pickVariant(CTA_COLOR_EXPERIMENT));
  const impressionFired = useRef(false);

  // Fire a one-shot impression for ALL A/B experiments (subtitle + headline + CTA color)
  useEffect(() => {
    if (impressionFired.current) return;
    impressionFired.current = true;
    const sid = getSessionId();
    [[HERO_EXPERIMENT, variant], [HEADLINE_EXPERIMENT, headlineVariant], [CTA_COLOR_EXPERIMENT, ctaVariant]].forEach(([exp, v]) => {
      api.post('/analytics/ab-event', {
        experiment: exp, variant: v, event: 'impression', session_id: sid,
      }).catch(() => {});
    });
  }, [variant, headlineVariant, ctaVariant]);

  const trackClick = (kind) => {
    const sid = getSessionId();
    [[HERO_EXPERIMENT, variant], [HEADLINE_EXPERIMENT, headlineVariant], [CTA_COLOR_EXPERIMENT, ctaVariant]].forEach(([exp, v]) => {
      api.post('/analytics/ab-event', {
        experiment: exp, variant: v, event: kind, session_id: sid,
      }).catch(() => {});
    });
  };

  return (
    <section
      data-testid="home-hero"
      className="relative min-h-screen flex items-end lg:items-center pt-28 pb-16 overflow-hidden bg-background"
    >
      {/* Book background image - Ken Burns / parallax to mimic the "flowmap deformation" effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 hero-book-bg" style={{ backgroundImage: 'url(/img/hero-book.jpg)' }} />
        <div className="absolute inset-0 hero-book-shine" />
        {/* Cyan glow accent from the light beam of the book */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 w-[900px] h-[900px] rounded-full bg-cyan-400/10 blur-[140px] pointer-events-none" />
        {/* Readability gradients */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(7,7,9,0.92) 0%, rgba(7,7,9,0.55) 45%, rgba(7,7,9,0.35) 100%)' }} />
        <div className="absolute inset-x-0 bottom-0 h-40" style={{ background: 'linear-gradient(180deg, rgba(7,7,9,0) 0%, rgba(7,7,9,0.95) 100%)' }} />
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative w-full">
        <div className="max-w-4xl">
          <div className="flex items-center gap-3 mb-6" data-testid="hero-brand">
            <img src="/dts-brain.png" alt="Digital Tech Solutions" className="w-14 h-14 object-contain animate-floaty" />
            <div className="leading-tight">
              <p className="font-display font-bold text-white text-lg">Digital Tech Solutions</p>
              <p className="text-lime text-xs font-mono-t">le nuove connessioni digitali</p>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass mb-7">
            <Sparkles size={14} className="text-lime" />
            <span className="text-white/80 text-xs font-mono-t">La Nuova Era Digitale</span>
          </div>

          <h1
            data-testid="hero-title"
            data-ab-headline={headlineVariant}
            className="font-display font-bold text-4xl md:text-6xl lg:text-[4.5rem] leading-[1.02] text-white mb-6"
          >
            {HEADLINE_VARIANTS[headlineVariant].html}
          </h1>

          <p data-testid="hero-subtitle" data-ab-variant={variant} className="text-white/75 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed">
            {HERO_VARIANTS[variant].subtitle}
          </p>

          <div className="flex flex-wrap gap-4">
            <Link
              to="/digital-score"
              onClick={() => trackClick('cta_primary')}
              data-testid="hero-cta-digital-score"
              data-ab-variant={variant}
              data-ab-cta={ctaVariant}
              className="group flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold btn-glow transition-all hover:scale-105"
              style={{
                background: CTA_COLORS[ctaVariant].bg,
                color: CTA_COLORS[ctaVariant].text,
              }}
            >
              <Sparkles size={16} /> Calcola il tuo Digital Score <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/servizi"
              onClick={() => trackClick('cta_secondary')}
              data-testid="hero-cta-servizi"
              data-ab-variant={variant}
              className="group flex items-center gap-2 px-7 py-3.5 rounded-full border-2 border-lime/60 text-white font-semibold hover:bg-lime hover:text-background transition-all hover:scale-105"
            >
              <Layers size={16} /> I nostri servizi <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
            </Link>
            <Link
              to="/contatti"
              onClick={() => trackClick('cta_secondary')}
              data-testid="hero-cta-consulenza"
              data-ab-variant={variant}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 text-white font-medium hover:border-lime hover:text-lime transition-colors backdrop-blur-md bg-white/5"
            >
              Prenota una consulenza
            </Link>
          </div>

          <div className="flex items-center gap-8 mt-14">
            <div><p className="font-display font-bold text-2xl text-white">1200+</p><p className="text-white/60 text-xs">Clienti soddisfatti</p></div>
            <div className="w-px h-10 bg-white/15" />
            <div><p className="font-display font-bold text-2xl text-white">95%</p><p className="text-white/60 text-xs">Successo progetti</p></div>
            <div className="w-px h-10 bg-white/15" />
            <div><p className="font-display font-bold text-2xl text-white">4.9</p><p className="text-white/60 text-xs">Rating medio</p></div>
          </div>
        </div>

        {/* 3D humanoid avatar removed per owner request (Oct 2026) */}
      </div>
    </section>
  );
}
